/**
 * Username helpers.
 *
 * Usernames are lowercase a-z and 0-9 only; 3-24 characters; must include
 * at least one letter (no all-numeric handles). Underscores, hyphens, and
 * spaces are not allowed. Server enforces the same rules via the
 * `validate_profile_username` trigger and `check_username_available` RPC.
 *
 * Usernames double as the user's affiliate referral ID (`?ref={username}`)
 * and are reserved for future public profiles at `/u/{username}`.
 *
 * Lifecycle:
 *  - First claim: one-shot via `claim_username` RPC.
 *  - Self-service rename: once every 365 days via `rename_username` RPC.
 *  - Super-admin override: any time via the `admin-rename-username` edge
 *    function (bypasses cooldown and the reservations block).
 *  - On rename, the previous handle is parked in `username_reservations`
 *    for 90 days as `former_handle` to prevent impersonation.
 */

import { supabase } from "@/integrations/supabase/client";

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 24;
export const USERNAME_PATTERN = /^[a-z0-9]{3,24}$/;


export type UsernameValidation =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateUsernameFormat(raw: string): UsernameValidation {
  const normalized = raw.trim().toLowerCase();
  if (normalized.length === 0) {
    return { ok: false, message: "Pick a username." };
  }
  if (normalized.length < USERNAME_MIN) {
    return { ok: false, message: `Must be at least ${USERNAME_MIN} characters.` };
  }
  if (normalized.length > USERNAME_MAX) {
    return { ok: false, message: `Must be ${USERNAME_MAX} characters or fewer.` };
  }
  if (!USERNAME_PATTERN.test(normalized)) {
    return {
      ok: false,
      message: "Use lowercase letters and numbers only \u2014 no underscores, hyphens, or spaces.",
    };
  }
  if (!/[a-z]/.test(normalized)) {
    return { ok: false, message: "Must include at least one letter." };
  }
  return { ok: true, normalized };
}

/** Server-side availability check. Resolves to false on any error. */
export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const v = validateUsernameFormat(username);
  if (!v.ok) return false;
  const { data, error } = await supabase.rpc("check_username_available", {
    _username: v.normalized,
  });
  if (error) {
    console.error("[username] check_username_available", error);
    return false;
  }
  return data === true;
}

/** Claim the username for a specific Persona (primary profile). One-shot. */
export async function claimUsername(profileId: string, username: string): Promise<void> {
  const v = validateUsernameFormat(username);
  if (!v.ok) throw new Error((v as { ok: false; message: string }).message);
  const normalized = v.normalized;
  const { error } = await supabase.rpc("claim_username", {
    _profile_id: profileId,
    _username: normalized,
  });
  if (error) {
    if (/USERNAME_RESERVED/.test(error.message)) {
      throw new Error("That username is reserved.");
    }
    if (/USERNAME_ALREADY_CLAIMED/.test(error.message)) {
      throw new Error("This Persona already has a personal username.");
    }
    if (/USERNAME_INVALID_FORMAT/.test(error.message)) {
      throw new Error("That username doesn't meet the format rules.");
    }
    if (/NOT_PERSONA_OWNER|NOT_A_PERSONA/.test(error.message)) {
      throw new Error("You can only claim a handle on a Persona you own.");
    }
    if (/duplicate key/i.test(error.message) || error.code === "23505") {
      throw new Error("That username is already taken.");
    }
    throw new Error(error.message);
  }
}

/** Rename the active Persona's username (subject to 365-day cooldown). */
export async function renameUsername(profileId: string, username: string): Promise<void> {
  const v = validateUsernameFormat(username);
  if (!v.ok) throw new Error((v as { ok: false; message: string }).message);
  const normalized = v.normalized;
  const { error } = await supabase.rpc("rename_username", {
    _profile_id: profileId,
    _username: normalized,
  });
  if (error) {
    if (/USERNAME_RENAME_COOLDOWN/.test(error.message)) {
      const match = /at (.+)$/.exec(error.message);
      const when = match?.[1] ? new Date(match[1].trim()) : null;
      throw new Error(
        when && !Number.isNaN(when.getTime())
          ? `You can rename again on ${when.toLocaleDateString()}.`
          : "You've already renamed your handle in the past year.",
      );
    }
    if (/USERNAME_NOT_CLAIMED/.test(error.message)) {
      throw new Error("Claim a username first before renaming it.");
    }
    if (/USERNAME_UNCHANGED/.test(error.message)) {
      throw new Error("That's already your handle.");
    }
    if (/USERNAME_RESERVED/.test(error.message)) {
      throw new Error("That username is reserved.");
    }
    if (/USERNAME_INVALID_FORMAT/.test(error.message)) {
      throw new Error("That username doesn't meet the format rules.");
    }
    if (/NOT_PERSONA_OWNER|NOT_A_PERSONA/.test(error.message)) {
      throw new Error("You can only rename a handle on a Persona you own.");
    }
    if (/duplicate key/i.test(error.message) || error.code === "23505") {
      throw new Error("That username is already taken.");
    }
    throw new Error(error.message);
  }
}

export type UsernameRenameStatus = {
  username: string | null;
  claimedAt: Date | null;
  changedAt: Date | null;
  /** Earliest moment the user can rename again. Null if never claimed. */
  nextEligibleAt: Date | null;
  /** True if the cooldown has elapsed (or no rename has happened yet). */
  canRenameNow: boolean;
};

/** Fetch rename eligibility for a specific Persona. */
export async function fetchUsernameRenameStatus(
  profileId: string,
): Promise<UsernameRenameStatus | null> {
  const { data, error } = await supabase.rpc("username_rename_status", {
    _profile_id: profileId,
  });
  if (error) {
    console.error("[username] username_rename_status", error);
    return null;
  }
  const row = (data ?? [])[0];
  if (!row) return null;
  const claimedAt = row.username_claimed_at ? new Date(row.username_claimed_at) : null;
  const changedAt = row.username_changed_at ? new Date(row.username_changed_at) : null;
  const nextEligibleAt = row.next_eligible_at ? new Date(row.next_eligible_at) : null;
  return {
    username: row.username ?? null,
    claimedAt,
    changedAt,
    nextEligibleAt,
    canRenameNow: !nextEligibleAt || nextEligibleAt.getTime() <= Date.now(),
  };
}

/**
 * Super-admin override: rename any user's handle, bypassing cooldown and
 * the reservations block. Format and live-collision checks still apply.
 */
export async function adminRenameUsername(
  targetUserId: string,
  newUsername: string,
): Promise<void> {
  const v = validateUsernameFormat(newUsername);
  if (!v.ok) throw new Error((v as { ok: false; message: string }).message);
  const { data, error } = await supabase.functions.invoke("admin-rename-username", {
    body: { target_user_id: targetUserId, new_username: v.normalized },
  });
  if (error) throw new Error(error.message);
  const payload = data as { ok?: boolean; error?: string } | null;
  if (payload?.error) throw new Error(payload.error);
  if (!payload?.ok) throw new Error("Rename failed.");
}

// ---------------------------------------------------------------------------
// Workspace brand handles
//
// Each workspace can optionally claim a separate `@brand_handle` that lives
// in the same global namespace as personal usernames. Same format rules,
// same 365-day rename cooldown, same 90-day former-handle reservation.
// ---------------------------------------------------------------------------

function mapBrandHandleError(err: { message: string; code?: string }): Error {
  const msg = err.message;
  if (/BRAND_HANDLE_RENAME_COOLDOWN/.test(msg)) {
    const match = /at (.+)$/.exec(msg);
    const when = match?.[1] ? new Date(match[1].trim()) : null;
    return new Error(
      when && !Number.isNaN(when.getTime())
        ? `You can rename this brand handle again on ${when.toLocaleDateString()}.`
        : "You've already renamed this brand handle in the past year.",
    );
  }
  if (/BRAND_HANDLE_NOT_CLAIMED/.test(msg)) {
    return new Error("Claim a brand handle first before renaming it.");
  }
  if (/BRAND_HANDLE_ALREADY_CLAIMED/.test(msg)) {
    return new Error("This workspace already has a brand handle.");
  }
  if (/BRAND_HANDLE_UNCHANGED/.test(msg)) {
    return new Error("That's already this workspace's handle.");
  }
  if (/BRAND_HANDLE_RESERVED/.test(msg)) {
    return new Error("That handle is reserved.");
  }
  if (/BRAND_HANDLE_TAKEN/.test(msg)) {
    return new Error("That handle is already taken.");
  }
  if (/BRAND_HANDLE_INVALID_FORMAT/.test(msg)) {
    return new Error("That handle doesn't meet the format rules.");
  }
  if (/NOT_WORKSPACE_OWNER/.test(msg)) {
    return new Error("Only the workspace owner can change its brand handle.");
  }
  if (/duplicate key/i.test(msg) || err.code === "23505") {
    return new Error("That handle is already taken.");
  }
  return new Error(msg);
}

export async function claimBrandHandle(workspaceId: string, handle: string): Promise<void> {
  const v = validateUsernameFormat(handle);
  if (!v.ok) throw new Error((v as { ok: false; message: string }).message);
  const { error } = await supabase.rpc("claim_brand_handle", {
    _workspace_id: workspaceId,
    _handle: v.normalized,
  });
  if (error) throw mapBrandHandleError(error);
}

export async function renameBrandHandle(workspaceId: string, handle: string): Promise<void> {
  const v = validateUsernameFormat(handle);
  if (!v.ok) throw new Error((v as { ok: false; message: string }).message);
  const { error } = await supabase.rpc("rename_brand_handle", {
    _workspace_id: workspaceId,
    _handle: v.normalized,
  });
  if (error) throw mapBrandHandleError(error);
}

export type BrandHandleStatus = {
  brandHandle: string | null;
  claimedAt: Date | null;
  changedAt: Date | null;
  /** Earliest moment the workspace owner can rename again. Null if never renamed. */
  nextEligibleAt: Date | null;
  canRenameNow: boolean;
};

export async function fetchBrandHandleStatus(
  workspaceId: string,
): Promise<BrandHandleStatus | null> {
  const { data, error } = await supabase.rpc("brand_handle_rename_status", {
    _workspace_id: workspaceId,
  });
  if (error) {
    console.error("[brand-handle] brand_handle_rename_status", error);
    return null;
  }
  const row = (data ?? [])[0];
  if (!row) {
    return {
      brandHandle: null,
      claimedAt: null,
      changedAt: null,
      nextEligibleAt: null,
      canRenameNow: true,
    };
  }
  const claimedAt = row.brand_handle_claimed_at ? new Date(row.brand_handle_claimed_at) : null;
  const changedAt = row.brand_handle_changed_at ? new Date(row.brand_handle_changed_at) : null;
  const nextEligibleAt = row.next_eligible_at ? new Date(row.next_eligible_at) : null;
  return {
    brandHandle: (row.brand_handle as string | null) ?? null,
    claimedAt,
    changedAt,
    nextEligibleAt,
    canRenameNow: !nextEligibleAt || nextEligibleAt.getTime() <= Date.now(),
  };
}
